import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, Paper, Button, CircularProgress } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { useNotification } from "../../providers/NotificationProvider";
import { fetchBusinessUnit } from "../../api/memberApi";

const formatDate = (iso) => {
  if (!iso) return "—";
  return iso.substring(0, 10).replace(/-/g, "/");
};

function DetailRow({ label, value }) {
  return (
    <Box
      sx={{
        display: "flex",
        py: 1.5,
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      <Typography
        variant="body2"
        sx={{ width: 200, flexShrink: 0, color: "text.secondary", fontWeight: 500 }}
      >
        {label}
      </Typography>
      <Typography variant="body2">{value || "—"}</Typography>
    </Box>
  );
}

export default function BusinessUnitDetailPage() {
  const { businessDomainId, businessUnitId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      setItem(await fetchBusinessUnit(businessUnitId));
    } catch (e) {
      notify.apiError(e);
    } finally {
      setLoading(false);
    }
  }, [businessUnitId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  if (!item) return null;

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/design/${businessDomainId}`)}
          variant="text"
        >
          {t("common.back")}
        </Button>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {t("businessUnit.title")}
        </Typography>
      </Box>
      <Paper variant="outlined" sx={{ p: 3 }}>
        <DetailRow label={t("businessDomain.title")} value={item.businessDomainName} />
        <DetailRow label={t("businessUnit.name")} value={item.businessUnitName} />
        <DetailRow label={t("businessUnit.vision")} value={item.vision} />
        <DetailRow label={t("businessUnit.startDate")} value={formatDate(item.startDate)} />
        <DetailRow label={t("businessUnit.endDate")} value={formatDate(item.endDate)} />
        <DetailRow label={t("businessUnit.url")} value={item.url} />
      </Paper>
    </Box>
  );
}
